import { createExcelRenderer } from './excel-renderer';
import { createHtmlRenderer } from './html-renderer';
import { createIfcRenderer } from './ifc-renderer';
import { createImageRenderer } from './image-renderer';
import { createJsonRenderer } from './json-renderer';
import { createMarkdownRenderer } from './markdown-renderer';
import { createOfficeRenderer } from './office-renderer';
import { createPdfRenderer } from './pdf-renderer';

import type {
  DocumentExcelPaneHighlight,
  DocumentViewerHighlight,
} from '@aecom-ai-engineering/react-components';

export interface CreateRenderersOptions {
  /** PDF page-rectangle highlights (finding coordinates). */
  pdfHighlights?: DocumentViewerHighlight[];
  /** PDF initial page (1-based). */
  initialPage?: number;
  /** Excel cell/row/range highlight. */
  excelHighlight?: DocumentExcelPaneHighlight;
  /** Enable in-document search. Defaults to true for both PDF and Excel. */
  searchable?: boolean;
  /** Enable continuous scroll for PDF. Defaults to true. */
  continuousScroll?: boolean;
  /**
   * Pre-resolved read URL for a server-converted PDF. When set, Office
   * documents (doc, docx, ppt, pptx) render via DocumentPdfPane using this
   * URL instead of the client-side @cyntler/react-doc-viewer fallback.
   */
  convertedPdfUrl?: string;
}

/**
 * Build the `renderers` dictionary for a `<DocumentViewer>`. Each key maps a
 * file-type string (returned by the library's `detectFileType`) to a pane
 * component. PDF and Excel use the library's built-in panes with the options
 * forwarded; every other format wraps an existing monoweb viewer.
 */
export function createRenderers(options?: CreateRenderersOptions) {
  const pdfRenderer = createPdfRenderer({
    highlights: options?.pdfHighlights,
    initialPage: options?.initialPage ?? 1,
    searchable: options?.searchable,
    continuousScroll: options?.continuousScroll,
  });

  const excelRenderer = createExcelRenderer({
    highlight: options?.excelHighlight,
    searchable: options?.searchable,
  });

  const officeRenderer = createOfficeRenderer({
    convertedPdfUrl: options?.convertedPdfUrl,
  });

  const imageRenderer = createImageRenderer();
  const ifcRenderer = createIfcRenderer();
  const htmlRenderer = createHtmlRenderer();
  const jsonRenderer = createJsonRenderer();
  const markdownRenderer = createMarkdownRenderer();

  return {
    pdf: pdfRenderer,
    excel: excelRenderer,

    doc: officeRenderer,
    docx: officeRenderer,
    ppt: officeRenderer,
    pptx: officeRenderer,
    odt: officeRenderer,
    ods: officeRenderer,
    odp: officeRenderer,

    jpg: imageRenderer,
    jpeg: imageRenderer,
    png: imageRenderer,
    gif: imageRenderer,
    webp: imageRenderer,

    html: htmlRenderer,
    json: jsonRenderer,
    md: markdownRenderer,
    markdown: markdownRenderer,

    ifc: ifcRenderer,
  };
}
